<!--
// Copyright © 2023 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
-->
<script lang="ts">
  import type { ThemeMode, ThemePreview } from '@hcengineering/theme'
  import { onMount } from 'svelte'
  import CheckCircled from './icons/CheckCircled.svelte'
  import FontSize from './icons/FontSize.svelte'

  export let theme: string
  export let focused: string
  export let mode: ThemeMode | undefined = undefined
  export let preview: ThemePreview | undefined = undefined

  let btn: HTMLButtonElement
  const fallbackPreview: ThemePreview = { tint: '#F8F9FA', base: '#FFFFFF', accent: '#3364E2', text: '#0F121A' }

  $: themeMode = mode ?? (theme === 'theme-system' ? 'system' : theme === 'theme-dark' ? 'dark' : 'light')
  $: selectedPreview = preview ?? fallbackPreview

  onMount(() => {
    if (focused === theme) btn.focus()
  })
</script>

<button
  bind:this={btn}
  class="antiButton regular sh-no-shape jf-center bs-none no-focus statusPopupThemeButton"
  class:focused={theme === focused}
  class:both={themeMode === 'system'}
  style:--preview-tint={selectedPreview.tint}
  style:--preview-base={selectedPreview.base}
  style:--preview-accent={selectedPreview.accent}
  style:--preview-text={selectedPreview.text}
>
  {#if themeMode === 'system'}
    <div class="light-container">
      <div class="paper"><FontSize size={'small'} /></div>
    </div>
    <div class="dark-container">
      <div class="paper"><FontSize size={'small'} /></div>
    </div>
  {:else}
    <div class="palette-container" class:dark={themeMode === 'dark'}>
      <div class="palette-sidebar" />
      <div class="palette-paper"><FontSize size={'small'} /></div>
      <div class="palette-accent" />
    </div>
  {/if}
  {#if focused === theme}
    <CheckCircled />
  {/if}
</button>

<style lang="scss">
  :global(.statusPopupThemeButton svg.check) {
    position: absolute;
    bottom: 3px;
    right: 3px;
    width: 16px;
    height: 16px;
  }
  .statusPopupThemeButton {
    position: relative;
    flex-shrink: 0;
    width: 76px;
    height: 56px;
    border-radius: 6px;

    .palette-container {
      position: relative;
      overflow: hidden;
      width: 100%;
      height: 100%;
      border-radius: 5.75px;
      border: 1px solid rgba(0, 0, 0, 0.1);
      background-color: var(--preview-tint);

      &.dark {
        border-color: rgba(255, 255, 255, 0.1);
      }

      .palette-sidebar {
        position: absolute;
        inset: 0 auto 0 0;
        width: 22px;
        background-color: var(--preview-tint);
      }

      .palette-paper {
        position: absolute;
        right: 0;
        bottom: 0;
        padding: 6px 0 0 6px;
        width: 52px;
        height: 38px;
        color: var(--preview-text);
        background-color: var(--preview-base);
        border-top: 1px solid rgba(0, 0, 0, 0.16);
        border-left: 1px solid rgba(0, 0, 0, 0.16);
        border-radius: 4px 0px 5.5px 0px;
      }

      .palette-accent {
        position: absolute;
        left: 7px;
        bottom: 8px;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: var(--preview-accent);
      }

      &.dark .palette-paper {
        border-color: rgba(255, 255, 255, 0.16);
      }
    }

    .light-container {
      background-color: #f5f5f5;
      border: 1px solid rgba(0, 0, 0, 0.1);

      .paper {
        color: #000000cc;
        background-color: #fff;
        border-top: 1px solid rgba(0, 0, 0, 0.2);
        border-left: 1px solid rgba(0, 0, 0, 0.2);
      }
    }
    .dark-container {
      background-color: #3f3f3f;

      .paper {
        color: #ffffffcc;
        background-color: #161516;
        border-top: 1px solid rgba(255, 255, 255, 0.2);
        border-left: 1px solid rgba(255, 255, 255, 0.2);
      }
    }
    .light-container,
    .dark-container {
      overflow: hidden;
      height: 100%;

      .paper {
        padding: 6px 0 0 6px;
        width: 100%;
        height: 100%;
        border-radius: 4px 0px 5.5px 0px;
      }
    }
    &.both {
      .light-container {
        border-right: none;
        border-radius: 5.75px 0 0 5.75px;
      }
      .dark-container {
        border-radius: 0 5.75px 5.75px 0;
      }
      .light-container,
      .dark-container {
        width: 50%;

        .paper {
          margin: 16px 0 0 8px;
        }
      }
    }
    &:not(.both) {
      .dark-container {
        border: 1px solid rgba(255, 255, 255, 0.1);
      }
      .light-container,
      .dark-container {
        width: 100%;
        border-radius: 5.75px;

        .paper {
          margin: 16px 0 0 16px;
        }
      }
    }

    &:hover:not(:focus) {
      .palette-container .palette-paper {
        filter: brightness(0.97);
      }
      .palette-container.dark .palette-paper {
        filter: brightness(1.15);
      }
      .light-container .paper {
        color: #000;
        background-color: #f2f2f2;
      }
      .dark-container .paper {
        color: #fff;
        background-color: #222222;
      }
    }
    &.focused::before,
    &:focus::before {
      position: absolute;
      content: '';
      inset: -3px;
      border: 1px solid var(--primary-button-default);
      border-radius: 8.5px;
      cursor: default;
    }
  }
</style>
